/**
 * Page Cycles de Paie & Bulletins (Admin)
 */
import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Calculator, Plus, Play, Trash2, Download, FileText, Search, TrendingUp, Users,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const RUN_STATUS = {
  draft: { l: 'Brouillon', c: 'bg-slate-100 text-slate-700' },
  calculating: { l: 'Calcul…', c: 'bg-amber-100 text-amber-700' },
  calculated: { l: 'Calculé', c: 'bg-blue-100 text-blue-700' },
  validated: { l: 'Validé', c: 'bg-emerald-100 text-emerald-700' },
  closed: { l: 'Clôturé', c: 'bg-violet-100 text-violet-700' },
};

const fmt = (n, c = 'XOF') => `${(n || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} ${c}`;

function currentPeriod() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function NewRunDialog({ open, onOpenChange, onCreated }) {
  const [period, setPeriod] = useState(currentPeriod());
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!/^\d{4}-\d{2}$/.test(period)) {
      toast.error('Période invalide (YYYY-MM)');
      return;
    }
    try {
      setBusy(true);
      const { data } = await api.post('/payroll/runs', { period });
      toast.success(`Cycle ${data.period} prêt`);
      onCreated(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-run-dialog">
        <DialogHeader>
          <DialogTitle>Nouveau cycle de paie</DialogTitle>
          <DialogDescription>Indiquez le mois à traiter.</DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Label>Période (YYYY-MM)</Label>
          <Input type="month" value={period} onChange={e => setPeriod(e.target.value)} data-testid="run-period" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={busy} data-testid="create-run-submit">
            {busy ? 'Création…' : 'Créer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PayslipsTable({ runId }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!runId) return;
    try {
      setLoading(true);
      const { data } = await api.get('/payroll/payslips', { params: { run_id: runId } });
      setItems(data.items || []);
    } catch (e) {
      toast.error('Chargement bulletins impossible');
    } finally {
      setLoading(false);
    }
  }, [runId]);
  useEffect(() => { load(); }, [load]);

  const downloadPdf = async (ps) => {
    try {
      const res = await api.get(`/payroll/payslips/${ps.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `bulletin-${ps.period}-${ps.employee?.matricule || ps.employeeId.slice(0, 6)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Téléchargement échoué');
    }
  };

  const filtered = items.filter(p => {
    if (!search) return true;
    const s = search.toLowerCase();
    const e = p.employee || {};
    return (
      (e.firstName || '').toLowerCase().includes(s) ||
      (e.lastName || '').toLowerCase().includes(s) ||
      (e.matricule || '').toLowerCase().includes(s)
    );
  });

  if (loading) return <p className="text-center py-8 text-slate-500">Chargement…</p>;
  if (items.length === 0) {
    return (
      <div className="text-center py-12 border-2 border-dashed rounded-lg">
        <FileText className="w-12 h-12 mx-auto text-slate-300" />
        <p className="mt-3 text-slate-600 font-medium">Aucun bulletin</p>
        <p className="text-sm text-slate-400 mt-1">Lancez le calcul du cycle pour générer les bulletins.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="relative max-w-xs">
        <Search className="absolute left-2 top-2.5 w-4 h-4 text-slate-400" />
        <Input className="pl-8" placeholder="Rechercher employé…" value={search}
          onChange={e => setSearch(e.target.value)} data-testid="payslip-search" />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left py-2 px-3">Matricule</th>
              <th className="text-left py-2 px-3">Employé</th>
              <th className="text-right py-2 px-3">Brut</th>
              <th className="text-right py-2 px-3">Retenues</th>
              <th className="text-right py-2 px-3">Net à payer</th>
              <th className="text-right py-2 px-3">Charges patronales</th>
              <th className="text-right py-2 px-3">PDF</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => (
              <tr key={p.id} className="border-t hover:bg-slate-50/50" data-testid={`payslip-row-${p.id}`}>
                <td className="py-2 px-3 font-mono text-xs">{p.employee?.matricule || '—'}</td>
                <td className="py-2 px-3 font-medium">
                  {p.employee?.firstName} {p.employee?.lastName}
                  <div className="text-xs text-slate-500">{p.employee?.jobTitle || ''}</div>
                </td>
                <td className="py-2 px-3 text-right">{fmt(p.grossSalary, p.currency)}</td>
                <td className="py-2 px-3 text-right text-red-600">
                  -{fmt(p.employeeContributions + p.otherDeductions, p.currency)}
                </td>
                <td className="py-2 px-3 text-right font-bold text-emerald-700">{fmt(p.netToPay, p.currency)}</td>
                <td className="py-2 px-3 text-right text-violet-600">{fmt(p.employerCharges, p.currency)}</td>
                <td className="py-2 px-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => downloadPdf(p)}
                    data-testid={`download-payslip-${p.id}`}>
                    <Download className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function PayrollRunsPage() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [calculating, setCalculating] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/payroll/runs');
      setRuns(data.items || []);
      if (!selected && data.items?.length) setSelected(data.items[0]);
    } catch (e) {
      toast.error('Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, [selected]);
  useEffect(() => { load(); }, []); // eslint-disable-line

  const calculateRun = async (run) => {
    if (!window.confirm(`Calculer la paie ${run.period} ?\n(Les bulletins existants seront remplacés)`)) return;
    try {
      setCalculating(true);
      const { data } = await api.post(`/payroll/runs/${run.id}/calculate`);
      toast.success(`${data.count} bulletin(s) générés`);
      const upd = runs.map(r => r.id === run.id
        ? { ...r, status: 'calculated', payslipCount: data.count, totals: data.totals } : r);
      setRuns(upd);
      setSelected({ ...run, status: 'calculated', payslipCount: data.count, totals: data.totals });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur calcul');
    } finally {
      setCalculating(false);
    }
  };

  const deleteRun = async (run) => {
    if (!window.confirm(`Supprimer le cycle ${run.period} et ses bulletins ?`)) return;
    try {
      await api.delete(`/payroll/runs/${run.id}`);
      toast.success('Cycle supprimé');
      if (selected?.id === run.id) setSelected(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="payroll-runs-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Calculator className="w-7 h-7 text-indigo-600" /> Cycles de Paie
            </h1>
            <p className="text-slate-500 mt-1">Génération mensuelle des bulletins de paie.</p>
          </div>
          <Button onClick={() => setDialogOpen(true)} data-testid="new-run-btn">
            <Plus className="w-4 h-4 mr-2" /> Nouveau cycle
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Liste cycles */}
          <Card className="lg:col-span-1">
            <CardHeader><CardTitle className="text-base">Périodes</CardTitle></CardHeader>
            <CardContent className="space-y-2 max-h-[70vh] overflow-y-auto">
              {loading ? (
                <p className="text-sm text-slate-500">Chargement…</p>
              ) : runs.length === 0 ? (
                <p className="text-sm text-slate-500">Aucun cycle</p>
              ) : (
                runs.map(r => (
                  <button key={r.id}
                    onClick={() => setSelected(r)}
                    data-testid={`run-${r.period}`}
                    className={`w-full text-left p-3 rounded-lg border transition ${
                      selected?.id === r.id ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:bg-slate-50'
                    }`}>
                    <div className="flex items-center justify-between">
                      <span className="font-bold">{r.period}</span>
                      <Badge className={RUN_STATUS[r.status]?.c}>{RUN_STATUS[r.status]?.l || r.status}</Badge>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {r.payslipCount || 0} bulletin(s)
                    </div>
                  </button>
                ))
              )}
            </CardContent>
          </Card>

          {/* Détail cycle */}
          <Card className="lg:col-span-3">
            <CardContent className="p-6">
              {!selected ? (
                <div className="text-center py-12">
                  <Calculator className="w-12 h-12 mx-auto text-slate-300" />
                  <p className="mt-3 text-slate-600">Sélectionnez un cycle ou créez-en un nouveau.</p>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold">Cycle {selected.period}</h2>
                      <Badge className={`mt-2 ${RUN_STATUS[selected.status]?.c}`}>
                        {RUN_STATUS[selected.status]?.l}
                      </Badge>
                    </div>
                    <div className="flex gap-2">
                      {selected.status !== 'closed' && (
                        <Button onClick={() => calculateRun(selected)} disabled={calculating}
                          data-testid="calculate-run-btn">
                          <Play className="w-4 h-4 mr-2" />
                          {calculating ? 'Calcul…' : (selected.status === 'draft' ? 'Calculer' : 'Recalculer')}
                        </Button>
                      )}
                      {selected.status !== 'closed' && (
                        <Button variant="outline" onClick={() => deleteRun(selected)}
                          data-testid="delete-run-btn">
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* KPIs */}
                  {selected.totals && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="p-4 bg-slate-50 rounded-lg border">
                        <div className="text-xs text-slate-500 flex items-center gap-1">
                          <Users className="w-3 h-3" /> Bulletins
                        </div>
                        <div className="text-2xl font-bold mt-1">{selected.payslipCount || 0}</div>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                        <div className="text-xs text-blue-700">Masse salariale brute</div>
                        <div className="text-xl font-bold text-blue-900 mt-1">{fmt(selected.totals.gross)}</div>
                      </div>
                      <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                        <div className="text-xs text-emerald-700 flex items-center gap-1">
                          <TrendingUp className="w-3 h-3" /> Net total à payer
                        </div>
                        <div className="text-xl font-bold text-emerald-900 mt-1">{fmt(selected.totals.net)}</div>
                      </div>
                      <div className="p-4 bg-violet-50 rounded-lg border border-violet-100">
                        <div className="text-xs text-violet-700">Charges patronales</div>
                        <div className="text-xl font-bold text-violet-900 mt-1">
                          {fmt(selected.totals.employer_charges)}
                        </div>
                      </div>
                    </div>
                  )}

                  <PayslipsTable runId={selected.id} />
                </>
              )}
            </CardContent>
          </Card>
        </div>

        <NewRunDialog open={dialogOpen} onOpenChange={setDialogOpen} onCreated={(r) => { setRuns([r, ...runs]); setSelected(r); }} />
      </div>
    </DashboardLayout>
  );
}
